<template>
  <section class="admin-content">
    <Navbar />
    <main class="admin-main">

      <!-- ── Page Header ─────────────────────────────────────── -->
      <div class="page-header">
        <div class="container">
          <div class="d-flex align-items-center p-t-40 p-b-40" style="gap: 16px;">
            <div class="header-icon-wrap">
              <i class="fe fe-file-text text-white" style="font-size: 1.4rem;"></i>
            </div>
            <div class="text-white">
              <h2 class="page-title mb-1">Encounter Details</h2>
              <p class="mb-0" style="opacity: .75; font-size: .9rem;"
                v-if="encounterDetails && encounterDetails.healthrecord">
                <i class="fe fe-hash mr-1"></i>{{ encounterDetails.healthrecord.encounter_id }}
              </p>
            </div>
          </div>
        </div>
      </div>

      <div class="vld-parent">
        <loading
          :active.sync="isLoading"
          loader="dots"
          :can-cancel="true"
          :is-full-page="fullPage"
        ></loading>
      </div>

      <div class="container mt-n3 pb-5" id="printDiv" ref="printNow">

        <!-- ── Action Bar ──────────────────────────────────────── -->
        <div class="card action-card shadow mb-4">
          <div class="card-body py-3 px-4">
            <div class="d-flex flex-wrap align-items-center" style="gap: 8px;">
              <button class="btn btn-sm btn-outline-secondary" @click="printMe">
                <i class="fe fe-printer mr-1"></i> Print
              </button>

              <!-- <button
                class="btn btn-sm btn-outline-secondary"
                data-toggle="modal"
                data-target="#example_01"
              >
                <i class="fe fe-send mr-1"></i> Request Authorization Code
              </button> -->

              <router-link :to="`/claim/${encounterDetails.claim_id}`" v-if="encounterDetails.claim_id != null">
                <button class="btn btn-sm btn-outline-danger">
                  <i class="mdi mdi-file-document-outline mr-1"></i> View Claim
                </button>
              </router-link>
            </div>
          </div>
        </div>

        <!-- ── Profile + Encounter ─────────────────────────────── -->
        <div class="row">

          <!-- Enrollee Profile -->
          <div class="col-md-4 mb-4">
            <div class="card shadow-sm enc-card h-100">
              <div class="enc-card-header">
                <i class="fe fe-user text-primary mr-2"></i>
                <span class="font-weight-bold" style="font-size: .85rem;">Enrollee Profile</span>
              </div>

              <div class="card-body text-center pb-2 pt-4">
                <component :is="enrolleeLink ? 'router-link' : 'div'" :to="enrolleeLink" class="enrollee-link"
                  title="Open patient page">
                  <img
                    v-if="encounterDetails.enrollee != null"
                    :src="`https://claimify-api.hayokmedicare.ng/image/${encounterDetails.enrollee.user_image}`"
                    alt="Enrollee Image"
                    class="enrollee-photo mb-2"
                    onerror="this.onerror=null; this.src='/assets/img/ohis_logo.png'"
                  />
  
                  <h5 class="font-weight-bold text-dark mb-0" v-if="encounterDetails.enrollee != null">
                    {{ encounterDetails.enrollee.lastname }} {{ encounterDetails.enrollee.firstname }}
                  </h5>
                </component>
              </div>

              <div class="list-group list-group-flush mt-2" v-if="encounterDetails.enrollee != null">
                <div class="info-row">
                  <i class="fe fe-hash info-icon"></i>
                  <div>
                    <p class="info-label">Insurance ID</p>
                    <p class="info-value">{{ encounterDetails.enrollee.id_card_number }}</p>
                  </div>
                </div>
                <div class="info-row">
                  <i class="fe fe-phone info-icon"></i>
                  <div>
                    <p class="info-label">Phone</p>
                    <p class="info-value">{{ encounterDetails.enrollee.phone_number }}</p>
                  </div>
                </div>
                <div class="info-row">
                  <i class="fe fe-gift info-icon"></i>
                  <div>
                    <p class="info-label">Date of Birth</p>
                    <p class="info-value">{{ encounterDetails.enrollee.dob | moment("Do MMMM, YYYY") }}</p>
                  </div>
                </div>
                <div class="info-row">
                  <i class="fe fe-users info-icon"></i>
                  <div>
                    <p class="info-label">Gender</p>
                    <p class="info-value">{{ encounterDetails.enrollee.gender }}</p>
                  </div>
                </div>
                <div class="info-row">
                  <i class="fe fe-clock info-icon"></i>
                  <div>
                    <p class="info-label">Encounter Time</p>
                    <p class="info-value text-primary font-weight-normal">
                      {{ encounterDetails.created_at | moment("from", "now") }}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Encounter Details -->
          <div class="col-md-8 mb-4">
            <div class="card shadow-sm enc-card h-100">
              <div class="enc-card-header">
                <i class="fe fe-activity text-primary mr-2"></i>
                <span class="font-weight-bold" style="font-size: .85rem;">Encounter Details</span>
              </div>

              <div v-if="encounterDetails.services" class="list-group list-group-flush">
                <div class="info-row">
                  <i class="fe fe-hash info-icon"></i>
                  <div>
                    <p class="info-label">Encounter ID</p>
                    <p class="info-value">{{ encounterDetails.healthrecord.encounter_id }}</p>
                  </div>
                </div>
                <div class="info-row">
                  <i class="fe fe-tag info-icon"></i>
                  <div>
                    <p class="info-label">Encounter Cadre</p>
                    <p class="info-value">{{ encounterDetails.treatment_type }}</p>
                  </div>
                </div>
                <div class="info-row" v-if="encounterDetails.diagnosis != null">
                  <i class="fe fe-alert-circle info-icon"></i>
                  <div>
                    <p class="info-label">Diagnosis</p>
                    <p class="info-value">{{ encounterDetails.diagnosis.name }}</p>
                  </div>
                </div>
                <div class="info-row" v-if="encounterDetails.secondarydiagnosis != null">
                  <i class="fe fe-alert-circle info-icon"></i>
                  <div>
                    <p class="info-label">Secondary Diagnosis</p>
                    <p class="info-value">{{ encounterDetails.secondarydiagnosis.name }}</p>
                  </div>
                </div>
                <div class="info-row" v-if="encounterDetails.secondarydiagnosis != null">
                  <i class="fe fe-alert-circle info-icon"></i>
                  <div>
                    <p class="info-label">Other Diagnosis</p>
                    <p class="info-value">{{ encounterDetails.secondarydiagnosis.name }}</p>
                  </div>
                </div>
                <div class="info-row">
                  <i class="fe fe-message-circle info-icon"></i>
                  <div>
                    <p class="info-label">Reason for Visit</p>
                    <p class="info-value">{{ encounterDetails.healthrecord.reasonVisit }}</p>
                  </div>
                </div>
                <div class="info-row">
                  <i class="fe fe-clipboard info-icon"></i>
                  <div>
                    <p class="info-label">Test Result</p>
                    <p class="info-value">{{ encounterDetails.healthrecord.testResult }}</p>
                  </div>
                </div>
                <div class="info-row">
                  <i class="fe fe-edit-3 info-icon"></i>
                  <div>
                    <p class="info-label">Notes</p>
                    <p class="info-value">{{ encounterDetails.healthrecord.notes }}</p>
                  </div>
                </div>
                <div class="info-row" v-if="encounterDetails.healthrecord.family_history">
                  <i class="fe fe-users info-icon"></i>
                  <div>
                    <p class="info-label">Family History, Allergies &amp; Others</p>
                    <p class="info-value">{{ encounterDetails.healthrecord.family_history }}</p>
                  </div>
                </div>
                <div class="info-row" v-if="encounterDetails.healthrecord.history_taking">
                  <i class="fe fe-file-text info-icon"></i>
                  <div>
                    <p class="info-label">History Taking</p>
                    <p class="info-value">{{ encounterDetails.healthrecord.history_taking }}</p>
                  </div>
                </div>
                <div class="info-row" v-if="encounterDetails.healthrecord.examination">
                  <i class="fe fe-search info-icon"></i>
                  <div>
                    <p class="info-label">Physical Examination</p>
                    <p class="info-value">{{ encounterDetails.healthrecord.examination }}</p>
                  </div>
                </div>
                <div class="info-row">
                  <i class="fe fe-clock info-icon"></i>
                  <div>
                    <p class="info-label">Date &amp; Time of Visit</p>
                    <p class="info-value">{{ encounterDetails.healthrecord.date_of_visit | moment("dddd, MMMM Do YYYY") }}</p>
                  </div>
                </div>
                <div class="info-row">
                  <i class="fe fe-home info-icon"></i>
                  <div>
                    <p class="info-label">Encounter Facility</p>
                    <p class="info-value">{{ encounterDetails.provider.agency_name }}</p>
                  </div>
                </div>
                <div class="info-row">
                  <i class="fe fe-user-check info-icon"></i>
                  <div>
                    <p class="info-label">Desk Officer</p>
                    <p class="info-value">{{ encounterDetails.healthrecord.professional.full_name }}</p>
                    <p class="text-muted mb-0" style="font-size:.8rem;">
                      {{ encounterDetails.healthrecord.professional.phone_number }}
                    </p>
                  </div>
                </div>
              </div>

              <div v-else class="card-body text-center text-muted py-5">
                <i class="fe fe-file-text" style="font-size: 2rem;"></i>
                <p class="mt-2 mb-0">No Encounter Records</p>
              </div>
            </div>
          </div>
        </div>

        <!-- ── Services / Drugs ──────────────────────────────── -->
        <div class="card shadow-sm enc-card mb-4"
          v-if="encounterDetails.services && encounterDetails.services.length">
          <div class="enc-card-header">
            <i class="fe fe-thermometer text-primary mr-2"></i>
            <span class="font-weight-bold" style="font-size: .85rem;">Service/Drugs Administered During Encounter</span>
            <span class="badge badge-soft-info ml-auto">{{ encounterDetails.services.length }} item(s)</span>
          </div>
          <div class="table-responsive">
            <table class="table align-td-middle mb-0">
              <thead class="table-head">
                <tr>
                  <th class="table-th">No.</th>
                  <th class="table-th">Type</th>
                  <th class="table-th">Name</th>
                  <th class="table-th">Distribution</th>
                  <th class="table-th">Unit Amount</th>
                  <th class="table-th">Amount</th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="(service, index) in encounterDetails.services"
                  v-bind:key="service.id"
                  class="table-row"
                >
                  <td class="px-3 py-3">{{ index + 1 }}</td>
                  <td class="py-3">
                    <span v-if="service.service != null" class="badge badge-soft-info" style="font-size:.72rem;">Service</span>
                    <span v-if="service.drug_id != null" class="badge badge-soft-warning" style="font-size:.72rem;">Drug</span>
                  </td>
                  <td class="py-3">
                    <span v-if="service.service != null">{{ service.service.description }}</span>
                    <span v-if="service.drug != null">{{ service.drug.drug_name }}</span>
                  </td>
                  <td class="py-3">
                    <span class="text-muted small">
                      (Freq = {{ service.frequency }}), (Dose = {{ service.dose }}), (Days = {{ service.days }})
                    </span>
                  </td>
                  <td class="py-3">
                    <span v-if="service.service != null">{{ service.service.price }}</span>
                    <span v-if="service.drug != null">{{ service.drug.price }}</span>
                  </td>
                  <td class="py-3 font-weight-bold">
                    <span v-if="service.service != null">
                      &#8358;{{ service.total_cost | numeral(0, 0) }}
                    </span>
                    <span v-if="service.drug != null">
                      &#8358;{{ service.total_cost | numeral(0, 0) }}</span
                    >
                  </td>
                </tr>
              </tbody>
              <tfoot>
                <tr style="background: #f8fafc;">
                  <td colspan="5" class="px-3 py-3 text-right font-weight-bold text-dark">Total Cost of Encounter</td>
                  <td class="py-3 font-weight-bold text-dark">&#8358;{{ totalServiceCharge | numeral(0, 0) }}</td>
                </tr>
                <tr style="background: #fff8e1;">
                  <td colspan="5" class="px-3 py-2 text-right text-warning font-weight-bold" style="font-size: .82rem;">
                    10% of Drug Charged to Customer
                  </td>
                  <td class="py-2 font-weight-bold text-warning" style="font-size: .82rem;">
                    &#8358;{{ tenPercent | numeral(0, 0) }}
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
        </div>

        <!-- Authorization Code Modal -->
        <div class="modal fade" id="example_01" tabindex="-1" role="dialog" aria-labelledby="example_02"
          aria-hidden="true">
          <div class="modal-dialog modal-dialog-centered modal-lg" role="document">
            <div class="modal-content" style="border-radius: 12px; overflow: hidden;">
              <div class="modal-header" style="background: linear-gradient(135deg,#1e3a5f,#2563eb);">
                <div class="d-flex align-items-center">
                  <div class="mr-3 d-flex align-items-center justify-content-center"
                    style="width:36px;height:36px;background:rgba(255,255,255,.15);border-radius:9px;">
                    <i class="fe fe-lock text-white"></i>
                  </div>
                  <h5 class="modal-title mb-0 text-white" id="example_02">Authorization Code Request</h5>
                </div>
                <button type="button" class="close text-white" data-dismiss="modal" aria-label="Close">
                  <span aria-hidden="true">&times;</span>
                </button>
              </div>
              <div class="modal-body">
                <div class="form-group">
                  <label class="small font-weight-bold text-muted text-uppercase">
                    <i class="fe fe-home mr-1"></i> Select Secondary Facility
                  </label>
                  <v-select v-model="referred_to_facility" label="agency_name" :options="providers"
                    placeholder="Search facility..." />
                </div>
              </div>
              <div class="modal-footer">
                <button type="button" class="btn btn-outline-secondary" data-dismiss="modal">
                  <i class="fe fe-x mr-1"></i> Cancel
                </button>
                <button class="btn btn-primary" @click="createAuthCode">
                  <i class="fe fe-send mr-1"></i> Submit
                </button>
              </div>
            </div>
          </div>
        </div>

      </div>
    </main>
  </section>
</template>

<script>
import Navbar from "@/views/Navbar.vue";
import Loading from "vue-loading-overlay";
// Import stylesheet
import "vue-loading-overlay/dist/vue-loading.css";

import { patientLink } from "@/utils/patientLink";
export default {
  components: {
    Loading,
    Navbar,
  },
  data() {
    return {
      encounterDetails: "",
      providers: "",
      patient: "",
      dependants: [],
      isLoading: false,
      encounterId: "",
      referred_to_facility: "",
      fullPage: true,
      payment_type: "online",
      selectedPaymentOption: "",
      paymentOptions: { online: "online", offline: "offline" },
    };
  },
  computed: {
    enrolleeLink() {
      return patientLink(this.encounterDetails && this.encounterDetails.enrollee);
    },
    totalServiceCharge() {
      let total = this.encounterDetails.total_drug_price;
      // this.encounterDetails.total_service_price;
      return total;
    },
    tenPercent() {
      let total = this.encounterDetails.total_drug_price;
      let calc = total * 0.1;
      return calc;
    },
  },

  methods: {
    getUserEncounterDetails() {
      this.isLoading = true;
      this.axios
        .get(`/api/v1/auth/service_summary/${this.$route.params.id}`)
        .then((response) => {
          this.encounterDetails = response.data;
          console.log(this.encounterDetails);
        })
        .catch((error) => {
          console.error(error);
        });
      this.isLoading = false;
    },

    dependentChar(index) {
      return String.fromCharCode("A".charCodeAt(0) + index);
    },

    createClaim() {
      if (confirm("Are you Sure you want to create Claim?")) {
        this.isLoading = true;
        this.axios
          .post(`/api/v1/auth/createClaimfromEncounter`, {
            encounter_id: this.$route.params.id,
          })
          .then((response) => {
            console.log(response);
            this.$toasted.info("Created Successfully!", {
              position: "top-center",
              duration: 3000,
            });
            this.getUserEncounterDetails();
          })
          .catch((error) => {
            console.error(error);
            this.$toasted.error("Error!", {
              position: "top-center",
              duration: 3000,
            });
          });
        this.isLoading = false;
      }
    },
    createAuthCode() {
      if (confirm("Are you sure you want to submit request")) {
        this.isLoading = true;
        this.axios
          .post("/api/v1/auth/authorization_code", {
            agency_id: 407465,
            principal_id: this.encounterDetails.client_id,
            dependent_id: this.encounterDetails.dependent_id,
            provider_id: this.encounterDetails.provider_id,
            referred_to_facility: this.referred_to_facility.id,
            service_summary_id: this.encounterDetails.id,
            org_id:
              this.encounterDetails.patient == null
                ? this.encounterDetails.dependent.user.org_id
                : this.encounterDetails.patient.org_id,
          })
          .then((response) => {
            console.log(response);
            this.isLoading = false;
            this.getUserEncounterDetails();
            // this.sendSMS();
            this.$toasted.info(`${response.data.message}`, {
              position: "top-center",
              duration: 3000,
            });
          })
          .catch((error) => {
            console.log(error.response);
            this.isLoading = false;
            this.$breadstick.notify("Oops! something went wrong", {
              position: "top-right",
            });
          });
      }
    },
    sendSMS() {
      this.isLoading = true;
      let message = `your code requested is successful. Go to https://app.oshia.ng/authorization-code  `;
      this.axios
        .post(
          `https://api.bulksmslive.com/v2/app/sms?email=faisalnas7@gmail.com&password=skrull123&sender_name=OHIS&message=${message}&recipients=${this.encounterDetails.creator.phone_number}`,
          {}
        )
        .then((response) => {
          console.log(response);
          let reply = response.data.msg;
          this.clearIt();
          this.isLoading = false;
          this.$toasted.info(`${reply}`, {
            position: "top-center",
            duration: 3000,
          });
        })
        .catch((error) => {
          console.log(error.response);
        });
    },
    printMe() {
      var printContents = document.getElementById("printDiv").innerHTML;
      var originalContents = document.body.innerHTML;
      document.body.innerHTML = printContents;
      window.print();
      document.body.innerHTML = originalContents;
    },
    getProviders() {
      this.axios
        .get(`/api/v1/auth/providerAgency/407465`)
        .then((response) => {
          this.providers = response.data.data;
          console.log(response);
        })
        .catch((error) => {
          console.error(error);
        });
    },
  },

  created() {
    this.getUserEncounterDetails();
    this.getProviders();
  },
};
</script>
<style scoped>
.enrollee-link { display: block; text-decoration: none; }
.enrollee-link:hover h5 { color: #2563eb !important; text-decoration: underline; }

@import url("https://fonts.googleapis.com/css2?family=Nunito:wght@200;300;400;600;700;800;900&display=swap");

html {
  box-sizing: border-box;
  font-size: 62.5%;
  font-family: "Nunito";
}

.spacer {
  margin-top: 10px;
  margin-bottom: 10px;
}

.heading {
  font-size: 1.3rem;
}

.jumbotron {
  background: rgb(72, 179, 120);
  text-align: center;
  color: #fff;
}

.CTA__prompt {
  font-size: 1.4rem;
}

.client__details {
  display: flex;
  flex-direction: column;
  align-items: center;
}

.btn {
  margin-bottom: 2rem;
}

input {
  border: none;
  padding: 9px 12px;
  border: 1px solid rgba(60, 66, 87, 0.12);
  width: 50%;
  margin: 1rem 0;
  border-radius: 0.4rem;
  box-shadow: 0px 2px 5px rgba(0, 0, 0, 0.08), 0px 1px 1px rgba(0, 0, 0, 0.04);
}

select {
  border: none;
  padding: 9px 12px;
  border: 1px solid rgba(60, 66, 87, 0.12);
  width: 70%;
  margin: 1rem 0;
  border-radius: 0.4rem;
  box-shadow: 0px 2px 5px rgba(0, 0, 0, 0.08), 0px 1px 1px rgba(0, 0, 0, 0.04);
}

.user__details--container {
  height: fit-content;

  display: flex;
}

.user__details-header-and-renewal__CTA {
  display: flex;
  justify-content: space-between;
  width: 100%;
}

.user__details--container-main {
  display: flex;
  flex-direction: column;

  padding: 2rem;
  height: fit-content;
  flex: 1;
  align-items: center;
}

.name {
  font-size: 1.5rem;
  font-weight: 500;
  margin-bottom: 1rem;
  color: #697386;
}

.primary__detail {
  padding: 1rem 2rem;
  background: #fff;
  width: 80%;
  display: flex;
  justify-content: space-between;
  align-items: center;

  border: 1px solid rgba(60, 66, 87, 0.12);
  box-shadow: 0px 2px 5px rgba(0, 0, 0, 0.08), 0px 1px 1px rgba(0, 0, 0, 0.04);
  margin-bottom: 1rem;
  border-radius: 0.4rem;
}

.ussd {
  padding: 1rem 2rem;
  background: #fff;
  min-width: 90%;
  border: 1px solid rgba(60, 66, 87, 0.12);
  box-shadow: 0px 2px 5px rgba(0, 0, 0, 0.08), 0px 1px 1px rgba(0, 0, 0, 0.04);
  margin: 0 1rem 2rem 1rem;
  border-radius: 0.4rem;
  transition: ease-in-out 0.3s;
  word-wrap: break-word;
}

.fa-2x {
  color: #697386;
}

.primary__detail--text {
  font-weight: 500;
  text-align: right;
}

.enrollee__img {
  width: 65%;
  height: auto;
  margin-bottom: 1rem;
  border-radius: 0.4rem;
}

.user__-details--container-other {
  height: fit-content;
  flex: 2;

  padding: 2rem;
  box-shadow: 0px 15px 30px rgb(0 0 0 / 8%);
}

.other__details--heading {
  font-size: 1.5rem;
  font-weight: 500;
  color: #697386;
}

.other__detail {
  border-bottom: 1px solid rgba(60, 66, 87, 0.12);
  margin-top: 1rem;
}

.image__container {
  display: flex;
  justify-content: center;
}

@media screen and (max-width: 64rem) /* 1024/16 = 64 rem */ {
  .user__details--container-main {
    flex: 2;
  }

  .primary__detail {
    width: 100%;
  }
}

@media screen and (max-width: 48.1rem) /* 769/16 = 48.1 rem */ {
  .user__details--container {
    display: block;
    padding: 2rem 1rem;
  }

  .user__details--container-main {
    padding: 0;
  }

  .fa-2x {
    font-size: 1.5rem;
  }

  input {
    width: 92%;
  }

  select {
    width: 100%;
  }
}
</style>
